import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Challenge, Milestone, Referral, Reward, StreakState } from '../types/membership';
import { rewardsService } from '../services/membership/rewardsService';
import { referralService } from '../services/membership/referralService';

interface RewardsContextType {
  streak: StreakState;
  rewards: Reward[];
  milestones: Milestone[];
  challenges: Challenge[];
  referral: Referral;
  canClaimDaily: boolean;
  claimDaily: () => Promise<number>;
  claimReward: (id: string) => Promise<void>;
  claimMilestone: (id: string) => Promise<void>;
  recordMetric: (metric: string, amount?: number) => void;
  joinChallenge: (id: string) => Promise<void>;
  refresh: () => void;
}

const RewardsContext = createContext<RewardsContextType | undefined>(undefined);

export function RewardsProvider({ children }: { children: ReactNode }) {
  const [streak, setStreak] = useState<StreakState>(rewardsService.getStreak());
  const [rewards, setRewards] = useState<Reward[]>(rewardsService.getRewards());
  const [milestones, setMilestones] = useState<Milestone[]>(rewardsService.getMilestones());
  const [challenges, setChallenges] = useState<Challenge[]>(rewardsService.getChallenges());
  const [referral, setReferral] = useState<Referral>(referralService.getReferral());
  const [canClaimDaily, setCanClaimDaily] = useState<boolean>(rewardsService.canClaimDaily());

  const refresh = useCallback(() => {
    setStreak(rewardsService.getStreak());
    setRewards(rewardsService.getRewards());
    setMilestones(rewardsService.getMilestones());
    setChallenges(rewardsService.getChallenges());
    setReferral(referralService.getReferral());
    setCanClaimDaily(rewardsService.canClaimDaily());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const claimDaily = useCallback(async () => {
    const { tokensAwarded } = await rewardsService.claimDailyReward();
    refresh();
    return tokensAwarded;
  }, [refresh]);

  const claimReward = useCallback(
    async (id: string) => {
      setRewards(await rewardsService.claimReward(id));
    },
    [],
  );

  const claimMilestone = useCallback(async (id: string) => {
    setMilestones(await rewardsService.claimMilestone(id));
  }, []);

  const recordMetric = useCallback(
    (metric: string, amount = 1) => {
      rewardsService.recordMetric(metric, amount);
      setMilestones(rewardsService.getMilestones());
    },
    [],
  );

  const joinChallenge = useCallback(async (id: string) => {
    setChallenges(await rewardsService.joinChallenge(id));
  }, []);

  return (
    <RewardsContext.Provider
      value={{
        streak,
        rewards,
        milestones,
        challenges,
        referral,
        canClaimDaily,
        claimDaily,
        claimReward,
        claimMilestone,
        recordMetric,
        joinChallenge,
        refresh,
      }}
    >
      {children}
    </RewardsContext.Provider>
  );
}

export function useRewardsContext() {
  const ctx = useContext(RewardsContext);
  if (!ctx) throw new Error('useRewardsContext must be used within a RewardsProvider');
  return ctx;
}
